import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Inbox, Users, MessageSquare, MailOpen, Trash2, ExternalLink, FileText, Calendar, HandHeart, Heart, Reply as ReplyIcon, Building2, Eye, Download, Loader2 } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useInbox } from '../context/InboxContext';
import { useToast } from '../hooks/use-toast';
import ConfirmDialog from '../components/ConfirmDialog';
import ReplyDialog from '../components/ReplyDialog';

const StatCard = ({ icon: Icon, label, value, to }) => {
  const inner = (
    <div className="bg-white border border-[#e9e0cc] p-6 flex items-start justify-between hover:border-[#3a2359] hover:shadow-sm">
      <div>
        <p className="text-xs tracking-[0.2em] uppercase text-[#a68a5b]">{label}</p>
        <p className="font-display text-4xl text-[#2a1a45] mt-1">{value}</p>
      </div>
      <div className="w-10 h-10 rounded-full bg-[#f2ead6] text-[#3a2359] flex items-center justify-center">
        <Icon size={18} />
      </div>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
};

const AdminDashboard = () => {
  const { isAdmin, loading, user } = useAuth();
  const { summary: inbox, refresh: refreshInbox } = useInbox();
  const { toast } = useToast();
  const [regs, setRegs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [prayers, setPrayers] = useState([]);
  const [hallRentals, setHallRentals] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [counts, setCounts] = useState({ bulletins: 0, news: 0, ministries: 0, team: 0 });
  const [visitorStats, setVisitorStats] = useState(null);
  const [tab, setTab] = useState('registrations');
  const [confirm, setConfirm] = useState(null);
  const [reply, setReply] = useState(null); // {to, subject, message, name, referenceType, referenceId}
  const [smtpOk, setSmtpOk] = useState(false);
  const [downloadingBackup, setDownloadingBackup] = useState(false);
  const [driveInfo, setDriveInfo] = useState(null);
  const [migrating, setMigrating] = useState(false);

  const loadDriveStatus = async () => {
    try {
      const res = await api.driveStatus();
      setDriveInfo(res.data);
    } catch (_e) {
      // Non-admin or backend down — silently skip.
    }
  };

  const handleMigrateToDrive = async () => {
    if (!window.confirm('Upload every existing image and PDF on the site to Google Drive? This runs once and only migrates records that haven\'t been migrated yet.')) return;
    setMigrating(true);
    try {
      const res = await api.driveMigrate();
      const s = res.data || {};
      const summary = [
        `Ministries: ${s.ministries || 0}`,
        `News: ${s.news || 0}`,
        `Page tiles: ${s.page_tiles || 0}`,
        `I'm New tiles: ${s.im_new_tiles || 0}`,
        `Church images: ${s.churches || 0}`,
        `Page images: ${s.pages || 0}`,
        `Bulletins: ${s.bulletins || 0}`,
        `Documents: ${s.documents || 0}`,
        s.errors?.length ? `Errors: ${s.errors.length}` : null,
      ].filter(Boolean).join(' • ');
      toast({ title: 'Drive migration finished', description: summary });
      loadDriveStatus();
    } catch (e) {
      toast({ title: 'Migration failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
    } finally {
      setMigrating(false);
    }
  };

  const handleDownloadBackup = async () => {
    setDownloadingBackup(true);
    try {
      const res = await api.downloadMediaBackup();
      // Extract filename from Content-Disposition, fallback to a sensible default
      const disp = res.headers?.['content-disposition'] || '';
      const match = disp.match(/filename="?([^"]+)"?/i);
      const filename = (match && match[1]) || `all-saints-images-${new Date().toISOString().slice(0, 10)}.zip`;
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast({ title: 'Backup ready', description: `Downloaded ${filename}` });
    } catch (e) {
      toast({ title: 'Backup failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
    } finally {
      setDownloadingBackup(false);
    }
  };

  const load = async () => {
    try {
      const [r, c, p, hr, b, n, m, t, es, vs, subs] = await Promise.all([
        api.listRegistrations(),
        api.listContacts(),
        api.listPrayers(),
        api.listHallRentals(),
        api.listBulletins(),
        api.listNews(),
        api.listMinistries(),
        api.listTeam(),
        api.emailStatus().catch(() => ({ data: { configured: false } })),
        api.visitorStats().catch(() => ({ data: null })),
        api.listBulletinSubscribers().catch(() => ({ data: [] })),
      ]);
      setRegs(r.data);
      setContacts(c.data);
      setPrayers(p.data);
      setHallRentals(hr.data);
      setCounts({ bulletins: b.data.length, news: n.data.length, ministries: m.data.length, team: t.data.length });
      setSmtpOk(!!es.data.configured);
      setVisitorStats(vs.data);
      setSubscribers(subs.data);
    } catch (e) {
      // handled by interceptor
    }
  };

  useEffect(() => { if (isAdmin) { load(); loadDriveStatus(); } }, [isAdmin]);

  if (loading) return null;
  if (!isAdmin) return <Navigate to="/admin/login" replace state={{ from: '/admin' }} />;

  const doMarkRead = async (id) => {
    await api.markContactRead(id);
    setContacts(cs => cs.map(x => x.id === id ? { ...x, read: true } : x));
    refreshInbox();
  };

  const doMarkPrayerRead = async (id) => {
    await api.markPrayerRead(id);
    setPrayers(ps => ps.map(x => x.id === id ? { ...x, read: true } : x));
    refreshInbox();
  };

  const doMarkHallRentalRead = async (id) => {
    await api.markHallRentalRead(id);
    setHallRentals(hs => hs.map(x => x.id === id ? { ...x, read: true } : x));
    refreshInbox();
  };

  const doMarkRegistrationRead = async (id) => {
    await api.markRegistrationRead(id);
    setRegs(rs => rs.map(x => x.id === id ? { ...x, read: true } : x));
    refreshInbox();
  };

  const doDelete = async () => {
    if (!confirm) return;
    try {
      if (confirm.kind === 'registration') {
        await api.deleteRegistration(confirm.id);
        setRegs(rs => rs.filter(r => r.id !== confirm.id));
        toast({ title: 'Registration deleted' });
      } else if (confirm.kind === 'contact') {
        await api.deleteContact(confirm.id);
        setContacts(cs => cs.filter(c => c.id !== confirm.id));
        toast({ title: 'Message deleted' });
      } else if (confirm.kind === 'prayer') {
        await api.deletePrayer(confirm.id);
        setPrayers(ps => ps.filter(p => p.id !== confirm.id));
        toast({ title: 'Prayer request deleted' });
      } else if (confirm.kind === 'hallRental') {
        await api.deleteHallRental(confirm.id);
        setHallRentals(hs => hs.filter(h => h.id !== confirm.id));
        toast({ title: 'Hall rental request deleted' });
      } else if (confirm.kind === 'subscriber') {
        await api.deleteBulletinSubscriber(confirm.id);
        setSubscribers(ss => ss.filter(s => s.id !== confirm.id));
        toast({ title: 'Subscriber removed' });
      }
      refreshInbox();
    } finally {
      setConfirm(null);
    }
  };

  const fmtDate = (iso) => new Date(iso).toLocaleString('en-CA', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  const exportSubscribersCsv = () => {
    if (!subscribers.length) return;
    const rows = [['email', 'subscribed_at']];
    subscribers.forEach(s => rows.push([s.email, s.created_at]));
    // Escape quotes and commas
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulletin-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <>
      <section className="bg-[#2a1a45] text-white py-14">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-xs tracking-[0.25em] uppercase text-[#e6d4a8]">Admin</p>
          <h1 className="font-display text-4xl md:text-5xl mt-2">Dashboard</h1>
          <p className="text-sm text-[#d5c9a5] mt-2">Welcome back, {user?.username}. Manage inboxes and site content below.</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard icon={Inbox} label="Registrations" value={regs.length} />
          <StatCard icon={Heart} label="Prayers" value={prayers.length} />
          <StatCard icon={MessageSquare} label="Messages" value={contacts.length} />
          <StatCard icon={Building2} label="Hall Rentals" value={hallRentals.length} />
        </div>

        {/* Site Visitors — anonymous privacy-friendly counter */}
        <div className="mb-10 bg-white border border-[#e9e0cc] p-6" data-testid="visitor-stats-card">
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <div className="flex items-center gap-2">
                <Eye size={16} className="text-[#3a2359]" />
                <p className="text-xs tracking-[0.2em] uppercase text-[#a68a5b] font-medium">Site Visitors</p>
              </div>
              <p className="text-[11px] text-[#78716c] mt-1">Anonymous — IP addresses are hashed. One visit is counted per browser session.</p>
            </div>
          </div>

          {visitorStats ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Today',       total: visitorStats.total_today,    unique: visitorStats.unique_today },
                { label: 'Last 7 days', total: visitorStats.total_week,     unique: visitorStats.unique_week },
                { label: 'Last 30 days',total: visitorStats.total_month,    unique: visitorStats.unique_month },
                { label: 'All time',    total: visitorStats.total_all_time, unique: visitorStats.unique_all_time },
              ].map(row => (
                <div key={row.label} data-testid={`visitor-stat-${row.label.toLowerCase().replace(/\s+/g,'-')}`} className="border border-[#f2ead6] p-4">
                  <p className="text-[10px] tracking-[0.2em] uppercase text-[#a68a5b]">{row.label}</p>
                  <p className="font-display text-3xl text-[#2a1a45] mt-2 leading-none">{row.total}</p>
                  <p className="text-[11px] text-[#78716c] mt-1">visits</p>
                  <p className="text-[11px] text-[#3a2359] mt-2">{row.unique} <span className="text-[#78716c]">unique</span></p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#78716c]">Loading visitor stats…</p>
          )}
        </div>

        {/* Google Drive storage status */}
        <div className="bg-white border border-[#e9e0cc] p-6 mb-10">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-[#a68a5b] font-medium">Google Drive Storage</p>
              <p className="font-display text-2xl text-[#2a1a45] mt-1">
                {driveInfo?.configured && driveInfo?.reachable && 'Connected'}
                {driveInfo?.configured && !driveInfo?.reachable && 'Configured but unreachable'}
                {!driveInfo?.configured && 'Not configured'}
              </p>
              {!driveInfo?.configured && (
                <p className="text-sm text-[#78716c] mt-2 max-w-xl">
                  All uploaded images and PDFs go to a folder on the parish Google Drive. Add
                  your <code className="bg-[#f5efe0] px-1 rounded">GOOGLE_OAUTH_*</code> credentials
                  to <code className="bg-[#f5efe0] px-1 rounded">backend/.env</code> to enable uploads.
                </p>
              )}
              {driveInfo?.configured && !driveInfo?.reachable && (
                <p className="text-sm text-red-700 mt-2">{driveInfo.error || 'Could not reach Google Drive with the current credentials.'}</p>
              )}
              {driveInfo?.counts && Object.values(driveInfo.counts).some(v => v > 0) && (
                <div className="text-xs text-[#78716c] mt-2">
                  On Drive: {Object.entries(driveInfo.counts).map(([k, v]) => `${k}: ${v}`).join(' • ')}
                </div>
              )}
            </div>
            {driveInfo?.configured && driveInfo?.reachable && (
              <button
                type="button"
                onClick={handleMigrateToDrive}
                disabled={migrating}
                data-testid="drive-migrate-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 border border-[#3a2359] bg-[#3a2359] hover:bg-[#2a1a45] text-white disabled:opacity-60 disabled:cursor-wait text-sm"
              >
                {migrating ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} className="rotate-180" />}
                {migrating ? 'Migrating…' : 'Migrate legacy files to Drive'}
              </button>
            )}
          </div>
        </div>

        {/* Quick links */}
        <div className="mb-10 flex flex-wrap gap-3 text-sm">
          <Link to="/bulletin" className="inline-flex items-center gap-1.5 px-4 py-2 border border-[#e9e0cc] hover:border-[#3a2359] text-[#3a2359]"><FileText size={14}/> Manage Bulletins</Link>
          <Link to="/" className="inline-flex items-center gap-1.5 px-4 py-2 border border-[#e9e0cc] hover:border-[#3a2359] text-[#3a2359]"><Calendar size={14}/> Manage News</Link>
          <Link to="/parish-life" className="inline-flex items-center gap-1.5 px-4 py-2 border border-[#e9e0cc] hover:border-[#3a2359] text-[#3a2359]"><HandHeart size={14}/> Manage Ministries</Link>
          <Link to="/mass-times" className="inline-flex items-center gap-1.5 px-4 py-2 border border-[#e9e0cc] hover:border-[#3a2359] text-[#3a2359]"><Calendar size={14}/> Manage Mass Times</Link>
          <Link to="/about" className="inline-flex items-center gap-1.5 px-4 py-2 border border-[#e9e0cc] hover:border-[#3a2359] text-[#3a2359]"><Users size={14}/> Manage Clergy & Staff</Link>
          <button
            type="button"
            onClick={handleDownloadBackup}
            disabled={downloadingBackup}
            data-testid="download-images-backup"
            title="Download every image on the site as a zip"
            className="inline-flex items-center gap-1.5 px-4 py-2 border border-[#e9e0cc] hover:border-[#3a2359] text-[#3a2359] disabled:opacity-60 disabled:cursor-wait"
          >
            {downloadingBackup ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {downloadingBackup ? 'Preparing zip…' : 'Download Images Backup'}
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-[#e9e0cc] mb-6 flex-wrap">
          {[
            { k: 'registrations', label: `Registrations${inbox.registrations ? ` (${inbox.registrations} new)` : ` (${regs.length})`}` },
            { k: 'prayers', label: `Prayer Requests${inbox.prayers ? ` (${inbox.prayers} new)` : ` (${prayers.length})`}` },
            { k: 'contacts', label: `Messages${inbox.contacts ? ` (${inbox.contacts} new)` : ` (${contacts.length})`}` },
            { k: 'hallRentals', label: `Hall Rentals${inbox.hall_rentals ? ` (${inbox.hall_rentals} new)` : ` (${hallRentals.length})`}` },
            { k: 'subscribers', label: `Subscribers (${subscribers.length})` },
          ].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)} className={`px-4 py-2 text-sm ${tab === t.k ? 'text-[#3a2359] border-b-2 border-[#3a2359] -mb-px font-medium' : 'text-[#5a534a] hover:text-[#3a2359]'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'registrations' && (
          <div className="space-y-3">
            {regs.length === 0 && <p className="text-sm text-[#78716c] py-8 text-center">No registrations yet.</p>}
            {regs.map(r => (
              <div key={r.id} className={`bg-white border p-5 ${r.read ? 'border-[#e9e0cc]' : 'border-[#3a2359]'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display text-xl text-[#2a1a45]">{r.firstName} {r.lastName}</h3>
                      {!r.read && <span className="px-2 py-0.5 bg-[#3a2359] text-white text-[10px] tracking-wider uppercase">New</span>}
                    </div>
                    <p className="text-sm text-[#5a534a]">{r.email} · {r.phone}</p>
                    <p className="text-xs text-[#78716c] mt-1">{fmtDate(r.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    {!r.read && (
                      <button onClick={() => doMarkRegistrationRead(r.id)} title="Mark as read" className="text-[#3a2359] hover:text-[#4d2f75] p-1.5">
                        <MailOpen size={16} />
                      </button>
                    )}
                    <button onClick={() => setConfirm({ kind: 'registration', id: r.id, name: `${r.firstName} ${r.lastName}` })} className="text-[#a63a3a] hover:text-[#7a1c1c] p-1.5" title="Delete">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-[#3f3a34] mt-3">
                  <div><span className="text-[#a68a5b]">Parish:</span> {r.parish === 'st-marys' ? "St. Mary's (Ladysmith)" : "St. Joseph's (Chemainus)"}</div>
                  <div><span className="text-[#a68a5b]">Household:</span> {r.household}</div>
                  <div className="sm:col-span-2"><span className="text-[#a68a5b]">Address:</span> {r.address}, {r.city} {r.postal}</div>
                  {r.childrenNames && <div className="sm:col-span-2"><span className="text-[#a68a5b]">Children:</span> {r.childrenNames}</div>}
                  <div><span className="text-[#a68a5b]">Baptized:</span> {r.baptized}</div>
                  <div><span className="text-[#a68a5b]">Envelopes:</span> {r.envelopes}</div>
                  {r.interests?.length > 0 && <div className="sm:col-span-2"><span className="text-[#a68a5b]">Interests:</span> {r.interests.join(', ')}</div>}
                  {r.notes && <div className="sm:col-span-2"><span className="text-[#a68a5b]">Notes:</span> {r.notes}</div>}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'prayers' && (
          <div className="space-y-3">
            {prayers.length === 0 && <p className="text-sm text-[#78716c] py-8 text-center">No prayer requests yet.</p>}
            {prayers.map(p => (
              <div key={p.id} className={`bg-white border p-5 ${p.read ? 'border-[#e9e0cc]' : 'border-[#3a2359]'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#f2ead6] text-[#3a2359] flex items-center justify-center flex-shrink-0">
                      <Heart size={15} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-display text-lg text-[#2a1a45]">
                          {p.name?.trim() ? p.name : <span className="italic text-[#5a534a]">Anonymous</span>}
                        </h3>
                        {!p.read && <span className="px-2 py-0.5 bg-[#3a2359] text-white text-[10px] tracking-wider uppercase">New</span>}
                        {p.is_public && <span className="px-2 py-0.5 bg-[#a68a5b] text-white text-[10px] tracking-wider uppercase">Public prayer list</span>}
                      </div>
                      {p.email && <p className="text-sm text-[#5a534a] mt-1"><a href={`mailto:${p.email}`} className="underline">{p.email}</a></p>}
                      <p className="text-xs text-[#78716c] mt-1">{fmtDate(p.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {!p.read && (
                      <button onClick={() => doMarkPrayerRead(p.id)} title="Mark as read" className="text-[#3a2359] hover:text-[#4d2f75] p-1.5">
                        <MailOpen size={16} />
                      </button>
                    )}
                    {p.email && (
                      <button
                        onClick={() => setReply({ to: p.email, subject: `Your prayer intention`, message: p.intention, name: p.name, referenceType: 'prayer', referenceId: p.id })}
                        title="Reply from dashboard"
                        className="text-[#3a2359] hover:text-[#4d2f75] p-1.5"
                      >
                        <ReplyIcon size={16} />
                      </button>
                    )}
                    <button onClick={() => setConfirm({ kind: 'prayer', id: p.id, name: p.intention.slice(0, 40) })} className="text-[#a63a3a] hover:text-[#7a1c1c] p-1.5" title="Delete">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <p className="mt-3 text-sm text-[#3f3a34] leading-relaxed whitespace-pre-wrap italic">&ldquo;{p.intention}&rdquo;</p>
              </div>
            ))}
          </div>
        )}

        {tab === 'contacts' && (
          <div className="space-y-3">
            {contacts.length === 0 && <p className="text-sm text-[#78716c] py-8 text-center">No messages yet.</p>}
            {contacts.map(c => (
              <div key={c.id} className={`bg-white border p-5 ${c.read ? 'border-[#e9e0cc]' : 'border-[#3a2359]'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-display text-xl text-[#2a1a45]">{c.subject}</h3>
                      {!c.read && <span className="px-2 py-0.5 bg-[#3a2359] text-white text-[10px] tracking-wider uppercase">New</span>}
                    </div>
                    <p className="text-sm text-[#5a534a] mt-1">{c.name} · <a href={`mailto:${c.email}`} className="underline">{c.email}</a></p>
                    <p className="text-xs text-[#78716c] mt-1">{fmtDate(c.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    {!c.read && (
                      <button onClick={() => doMarkRead(c.id)} title="Mark as read" className="text-[#3a2359] hover:text-[#4d2f75] p-1.5">
                        <MailOpen size={16} />
                      </button>
                    )}
                    <button
                      onClick={() => setReply({ to: c.email, subject: c.subject, message: c.message, name: c.name, referenceType: 'contact', referenceId: c.id })}
                      title="Reply from dashboard"
                      className="text-[#3a2359] hover:text-[#4d2f75] p-1.5"
                    >
                      <ReplyIcon size={16} />
                    </button>
                    <button onClick={() => setConfirm({ kind: 'contact', id: c.id, name: c.subject })} className="text-[#a63a3a] hover:text-[#7a1c1c] p-1.5" title="Delete">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <p className="mt-3 text-sm text-[#3f3a34] leading-relaxed whitespace-pre-wrap">{c.message}</p>
              </div>
            ))}
          </div>
        )}

        {tab === 'hallRentals' && (
          <div className="space-y-3">
            {hallRentals.length === 0 && <p className="text-sm text-[#78716c] py-8 text-center">No hall rental requests yet.</p>}
            {hallRentals.map(h => (
              <div key={h.id} className={`bg-white border p-5 ${h.read ? 'border-[#e9e0cc]' : 'border-[#3a2359]'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#f2ead6] text-[#3a2359] flex items-center justify-center flex-shrink-0">
                      <Building2 size={15} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-display text-lg text-[#2a1a45]">{h.name}</h3>
                        {!h.read && <span className="px-2 py-0.5 bg-[#3a2359] text-white text-[10px] tracking-wider uppercase">New</span>}
                      </div>
                      <p className="text-sm text-[#5a534a] mt-1">
                        <a href={`mailto:${h.email}`} className="underline">{h.email}</a>
                        {h.phone && <> · <a href={`tel:${h.phone}`} className="underline">{h.phone}</a></>}
                      </p>
                      <p className="text-xs text-[#78716c] mt-1">{fmtDate(h.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {!h.read && (
                      <button onClick={() => doMarkHallRentalRead(h.id)} title="Mark as read" className="text-[#3a2359] hover:text-[#4d2f75] p-1.5">
                        <MailOpen size={16} />
                      </button>
                    )}
                    <button
                      onClick={() => setReply({ to: h.email, subject: `Re: your Hall Rental request`, message: h.message, name: h.name, referenceType: 'hallRental', referenceId: h.id })}
                      title="Reply from dashboard"
                      className="text-[#3a2359] hover:text-[#4d2f75] p-1.5"
                    >
                      <ReplyIcon size={16} />
                    </button>
                    <button onClick={() => setConfirm({ kind: 'hallRental', id: h.id, name: `${h.name} — ${h.event_type || 'request'}` })} className="text-[#a63a3a] hover:text-[#7a1c1c] p-1.5" title="Delete">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <dl className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                  {h.event_date && <div><dt className="text-[#a68a5b] uppercase tracking-wider text-[10px]">Date</dt><dd className="text-[#3f3a34]">{h.event_date}</dd></div>}
                  {h.event_type && <div><dt className="text-[#a68a5b] uppercase tracking-wider text-[10px]">Type</dt><dd className="text-[#3f3a34]">{h.event_type}</dd></div>}
                  {h.guest_count && <div><dt className="text-[#a68a5b] uppercase tracking-wider text-[10px]">Guests</dt><dd className="text-[#3f3a34]">{h.guest_count}</dd></div>}
                </dl>
                <p className="mt-3 text-sm text-[#3f3a34] leading-relaxed whitespace-pre-wrap">{h.message}</p>
              </div>
            ))}
          </div>
        )}

        {tab === 'subscribers' && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <p className="text-sm text-[#5a534a]">
                Parishioners who signed up on <span className="text-[#3a2359] font-medium">/bulletin</span> to receive the weekly bulletin by email.
              </p>
              <button
                type="button"
                onClick={exportSubscribersCsv}
                disabled={!subscribers.length}
                data-testid="export-subscribers-csv"
                className="inline-flex items-center gap-1.5 px-4 py-2 border border-[#3a2359] text-[#3a2359] text-sm hover:bg-[#3a2359] hover:text-white disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#3a2359] disabled:cursor-not-allowed"
              >
                <Download size={14} /> Export CSV
              </button>
            </div>
            {subscribers.length === 0 ? (
              <p className="text-sm text-[#78716c] py-8 text-center">No subscribers yet.</p>
            ) : (
              <div className="bg-white border border-[#e9e0cc] divide-y divide-[#f2ead6]" data-testid="subscribers-list">
                {subscribers.map(s => (
                  <div key={s.id} className="flex items-center justify-between gap-3 px-5 py-3">
                    <div className="min-w-0">
                      <p className="text-sm text-[#2a1a45] truncate">
                        <a href={`mailto:${s.email}`} className="hover:underline">{s.email}</a>
                      </p>
                      <p className="text-xs text-[#78716c] mt-0.5">Subscribed {fmtDate(s.created_at)}</p>
                    </div>
                    <button
                      onClick={() => setConfirm({ kind: 'subscriber', id: s.id, name: s.email })}
                      className="text-[#a63a3a] hover:text-[#7a1c1c] p-1.5"
                      title="Remove subscriber"
                      data-testid={`remove-subscriber-${s.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      <ConfirmDialog
        open={!!confirm}
        title={`Delete this ${confirm?.kind}?`}
        message={confirm ? `“${confirm.name}” will be permanently removed.` : ''}
        onCancel={() => setConfirm(null)}
        onConfirm={doDelete}
      />

      {reply && (
        <ReplyDialog
          open={!!reply}
          to={reply.to}
          originalSubject={reply.subject}
          originalMessage={reply.message}
          originalName={reply.name}
          referenceType={reply.referenceType}
          referenceId={reply.referenceId}
          smtpConfigured={smtpOk}
          onClose={() => setReply(null)}
          onSent={() => {
            // Mark read locally
            if (reply.referenceType === 'contact') {
              setContacts(cs => cs.map(c => c.id === reply.referenceId ? { ...c, read: true } : c));
            } else if (reply.referenceType === 'hallRental') {
              setHallRentals(hs => hs.map(h => h.id === reply.referenceId ? { ...h, read: true } : h));
            } else {
              setPrayers(ps => ps.map(p => p.id === reply.referenceId ? { ...p, read: true } : p));
            }
          }}
        />
      )}
    </>
  );
};

export default AdminDashboard;
